import { PeekViewService } from '@hive/core/modules/peek-view/services/peek-view';
import { AppThemeService } from '@hive/core/modules/theme';
import type { Container } from '@blocksuite/hive/global/di';
import { ColorScheme } from '@blocksuite/hive/model';
import {
  type ThemeExtension,
  ThemeExtensionIdentifier,
} from '@blocksuite/hive/shared/services';
import {
  createSignalFromObservable,
  type Signal,
  SpecProvider,
} from '@blocksuite/hive/shared/utils';
import {
  type BlockStdScope,
  LifeCycleWatcher,
  StdIdentifier,
} from '@blocksuite/hive/std';
import type { ExtensionType } from '@blocksuite/hive/store';
import type { FrameworkProvider } from '@toeverything/infra';
import type { Observable } from 'rxjs';

import { AIChatBlockSpec } from '../../ai/blocks';
import { AITranscriptionBlockSpec } from '../../ai/blocks/ai-chat-block/ai-transcription-block';
import { buildDocDisplayMetaExtension } from '../display-meta';
import { getFontConfigExtension } from '../font-config';
import { patchPeekViewService } from '../peek-view-service';
import { getThemeExtension } from '../theme';

function getPagePreviewThemeExtension(framework: FrameworkProvider) {
  class HivePagePreviewThemeExtension
    extends LifeCycleWatcher
    implements ThemeExtension
  {
    static override readonly key = 'hive-page-preview-theme';

    readonly theme: Signal<ColorScheme>;

    readonly disposables: (() => void)[] = [];

    static override setup(di: Container) {
      super.setup(di);
      di.override(ThemeExtensionIdentifier, HivePagePreviewThemeExtension, [
        StdIdentifier,
      ]);
    }

    constructor(std: BlockStdScope) {
      super(std);
      const theme$: Observable<ColorScheme> = framework
        .get(AppThemeService)
        .appTheme.theme$.map(theme => {
          return theme === ColorScheme.Dark
            ? ColorScheme.Dark
            : ColorScheme.Light;
        });
      const { signal, cleanup } = createSignalFromObservable<ColorScheme>(
        theme$,
        ColorScheme.Light
      );
      this.theme = signal;
      this.disposables.push(cleanup);
    }

    getAppTheme() {
      return this.theme;
    }

    getEdgelessTheme() {
      return this.theme;
    }

    override unmounted() {
      this.dispose();
    }

    dispose() {
      this.disposables.forEach(dispose => dispose());
    }
  }

  return HivePagePreviewThemeExtension;
}

const fontConfig = getFontConfigExtension();

let _framework: FrameworkProvider;
let _previewExtensions: ExtensionType[];
export function enablePreviewExtension(framework: FrameworkProvider): void {
  if (_framework === framework && _previewExtensions) {
    return;
  }

  const specProvider = SpecProvider._;

  if (_previewExtensions) {
    _previewExtensions.forEach(extension => {
      specProvider.omitSpec('preview:page', extension);
      specProvider.omitSpec('preview:edgeless', extension);
    });
  }

  _framework = framework;
  const peekViewService = framework.get(PeekViewService);

  _previewExtensions = [
    ...AIChatBlockSpec,
    ...AITranscriptionBlockSpec,
    fontConfig,
    getThemeExtension(framework),
    getPagePreviewThemeExtension(framework),
    buildDocDisplayMetaExtension(framework),
    patchPeekViewService(peekViewService),
  ];

  specProvider.extendSpec('preview:page', _previewExtensions);
  specProvider.extendSpec('preview:edgeless', _previewExtensions);
}
